enum MasterType {
    Maestro = 13,
    PayPal = 28
}

enum PaymentType {
    CreditCard = 1,
    DebitCard = 2,
    PayPal = 14
}

type BookingPaymentOption = Core.BookingPaymentOption;
type BookingSavedCardOption = Core.BookingSavedCardOption;
type BookingPaymentDetails = Core.BookingPaymentDetails;
type MybPaymentOption = Core.MybPaymentOption;
type MybSavedCardOption = Core.MybSavedCardOption;
type PaymentOption = Core.PaymentOption;
type SavedCardOption = Core.SavedCardOption;
type ExternalFopDetail = Core.ExternalFopDetail;
type PaymentModel = Core.PaymentModel;

export function createEmptyPaymentModel(): PaymentModel {
    return {
        cardDetail: {
            cardNumber: '',
            cardTypeId: undefined,
            expiryDate: {month: 0, year: 0},
            issueNumber: '',
            maskedNumber: '',
            paymentOptionId: 0,
            paymentTypeId: 0,
            securityCode: '',
            startDate: {month: 0, year: 0},
            isSavedCard: false,
            savedCardId: ''
        },
        externalFop: undefined,
        fee: 0,
        isPayPalPayment: false,
        saveCard: false
    };
}

export function createEmptyExternalFop(): ExternalFopDetail {
    return {authorisationCode: '', note: ''};
}

export function convertBookingPaymentOptions(options: BookingPaymentOption[]): PaymentOption[] {
    return _.map(options, o => {
        return {
            AdminOverride: o.AdminOverride,
            CardTypeId: o.CardTypeId,             //To keep mapping in payment service working
            FeeAmount: o.FeeAmount,
            FeePercent: o.FeePercent,
            FullDetailsRequired: o.FullDetails,
            Id: o.CardTypeId,
            Name: o.Title,
            PaymentType: o.PaymentTypeId,
            Title: o.Title,                       //To keep mapping in payment service working
            IsExternalFop: o.IsExternalFop,
            IsPayPal: o.PaymentTypeId === PaymentType.PayPal || o.CardTypeId === MasterType.PayPal
        };
    });
}

export function convertBookingSavedCards(savedOptions: BookingSavedCardOption[],
    paymentOptions: BookingPaymentOption[]): SavedCardOption[] {
    let options: SavedCardOption[] = [];

    _.each(savedOptions, so => {
        const paymentOption = _.find(paymentOptions, {'CardTypeId': so.CardTypeId});

        if (paymentOption) {
            options.push({
                CardId: so.CardId,
                CardName: so.CardName,
                CardType: so.CardTypeId,
                ExpiryMonth: so.ExpiryMonth,
                ExpiryYear: so.ExpiryYear,
                FeeAmount: paymentOption.FeeAmount, //why is this not from the API
                FeePercent: paymentOption.FeePercent, //why is this not from the API
                FullDetailsRequired: paymentOption.FullDetails,
                IsExpired: so.IsExpired,
                LogoName: so.LogoName,
                MaskedNumber: so.MaskedNumber,
                MasterCardTypeId: so.MasterCardTypeId,
                PaymentTypeId: paymentOption.PaymentTypeId
            });
        }
    });

    return options;
}

export function convertMybPaymentOptions(options: MybPaymentOption[]): PaymentOption[] {
    return _.map(options, o => {
        return {
            AdminOverride: o.AdminOverride,
            CardTypeId: o.CardType,             //To keep mapping in payment service working
            FeeAmount: o.FeeAmount,
            FeePercent: o.FeePercent,
            FullDetailsRequired: o.CardType === MasterType.Maestro,
            Id: o.Id,
            IsExternalFop: o.IsExternalFop,
            Name: o.Name,
            PaymentType: o.PaymentType,
            Title: o.Name,                       //To keep mapping in payment service working
            IsPayPal: o.PaymentType === PaymentType.PayPal || o.CardType === MasterType.PayPal
        };
    });
}

export function convertMybSavedCards(savedOptions: MybSavedCardOption[],
    paymentOptions: MybPaymentOption[]): SavedCardOption[] {
    let options: SavedCardOption[] = [];

    _.each(savedOptions, so => {
        //check we can find the card type info (why does the api not give us this??)
        const paymentOption = _.find(paymentOptions, {'CardType': so.MasterCardTypeId});

        if (paymentOption) {
            options.push({
                CardId: so.CardId,
                CardName: so.CardName,
                CardType: paymentOption.Id,  //crazy
                ExpiryMonth: so.ExpiryMonth,
                ExpiryYear: so.ExpiryYear,
                FeeAmount: paymentOption.FeeAmount, //why is this not from the API
                FeePercent: paymentOption.FeePercent, //why is this not from the API
                FullDetailsRequired: so.FullDetailsRequired,
                IsExpired: so.IsExpired,
                LogoName: so.LogoName,
                MaskedNumber: so.MaskedNumber,
                MasterCardTypeId: so.MasterCardTypeId,
                PaymentTypeId: paymentOption.PaymentType   //why is this not from the API
            });
        }
    });

    return options;
}

export function addPaymentDetailsToClearRequest(source: PaymentModel, target: any): void {
    var valueOrDefault = (v,d) => !_.isEmpty(v) ? v : d;
    var toStringOrDefault = (v,d) => v !== 0 ? v.toString() : d;

    //add our card details to the Clear Request object that is sent to the API where
    //we are allowed to use nulls
    let card = source.cardDetail;

    target.CardFeeAmount = source.fee;
    target.PaymentTypeId = card.paymentTypeId;
    target.PaymentOptionId = card.paymentOptionId;
    target.CardNumber = valueOrDefault(card.cardNumber, null);
    target.CardNumberMasked = valueOrDefault(card.maskedNumber, null);
    target.CardStartMonth = toStringOrDefault(card.startDate.month, null);
    target.CardStartYear = toStringOrDefault(card.startDate.year, null);
    target.CardEndMonth = toStringOrDefault(card.expiryDate.month, null);
    target.CardEndYear = toStringOrDefault(card.expiryDate.year, null);
    target.CardIssueNumber = valueOrDefault(card.issueNumber, null);
    target.CardCvc = valueOrDefault(card.securityCode, null);
    target.IsSavedCard = card.isSavedCard;
    target.SavedCardId = valueOrDefault(card.savedCardId, null);
    target.RetainCard = source.saveCard;
    target.ExternalAuthorisationCode = valueOrDefault(source.externalFop
        && source.externalFop.authorisationCode, null);
    target.ExternalPaymentNote = valueOrDefault(source.externalFop && source.externalFop.note, null);
}

export function copyCardDetailsToUserData(source: PaymentModel, target: BookingPaymentDetails): void {
    //use our payment model to fill the booking payment details that we use on the booking
    //form. Not ideal but until we can make the objects expected in  MYB and the BookingForm
    //the same we will have to provide this transform
    let t = target.CardDetails;
    let s = source.cardDetail;

    t.CardNumber = (s.cardNumber && s.cardNumber.length > 0 ? s.cardNumber : null);
    t.CardType = s.paymentOptionId;
    t.CardId = s.savedCardId;
    t.ExpiryDate = {
        Month: s.expiryDate.month,
        Year: s.expiryDate.year
    };
    t.IssueNumber = (s.issueNumber.length > 0 ? s.issueNumber : null);
    t.MaskedCardNumber = s.maskedNumber;
    t.PaymentTypeId = s.paymentTypeId;
    t.SecurityCode = (s.securityCode && s.securityCode.length > 0 ? s.securityCode : null);
    t.StartDate = {
        Month: s.startDate.month,
        Year: s.startDate.year
    };
    target.SaveThisCard = source.saveCard;
}
